import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { Download, RefreshCw, Building2, ShieldCheck } from "lucide-react";

export default function Settings() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ws, setWs] = useState(null);

  useEffect(() => { api.get("/workspace").then(({ data }) => setWs(data)).catch((e) => toast.error(apiErr(e))); }, []);

  const exportData = async () => {
    try {
      const [w, deps, team, systems] = await Promise.all([
        api.get("/workspace"), api.get("/dependencies"), api.get("/team"), api.get("/systems"),
      ]);
      const blob = new Blob([JSON.stringify({ workspace: w.data, dependencies: deps.data, team: team.data, systems: systems.data }, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "independent-export.json"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Datos exportados");
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="font-head text-2xl sm:text-3xl font-bold text-white">Ajustes</h1>
        <p className="mt-1 text-slate-400">Datos de la empresa, cuenta y privacidad (GDPR).</p>
      </div>

      <div className="card-arch p-6">
        <div className="flex items-center gap-2 text-cyan-400"><Building2 className="h-4 w-4" /><span className="mono-label">Empresa</span></div>
        {ws && (
          <div className="mt-4 grid sm:grid-cols-2 gap-y-3 gap-x-6 text-sm">
            {[["Nombre", ws.name], ["Sector", ws.sector], ["Empleados", ws.employee_count], ["Ubicaciones", ws.location_count], ["Rol", ws.owner_role], ["Objetivo", ws.main_goal]].map(([k, v]) => (
              <div key={k} className="flex justify-between border-b border-white/5 pb-2"><span className="text-slate-500">{k}</span><span className="text-white text-right">{v}</span></div>
            ))}
          </div>
        )}
        <Button data-testid="settings-reaudit" onClick={() => navigate("/audit?new=1")} className="mt-5 bg-white/[0.06] text-white hover:bg-white/[0.1] rounded-full font-semibold"><RefreshCw className="mr-2 h-4 w-4" /> Repetir diagnóstico</Button>
      </div>

      <div className="card-arch p-6">
        <div className="flex items-center gap-2 text-cyan-400"><ShieldCheck className="h-4 w-4" /><span className="mono-label">Privacidad y datos</span></div>
        <p className="mt-3 text-sm text-slate-400">Cuenta: {user?.email}</p>
        <div className="mt-4 flex flex-wrap gap-3">
          <Button data-testid="settings-export" onClick={exportData} variant="outline" className="bg-white/[0.03] border-white/10 hover:bg-white/[0.07] text-white rounded-full"><Download className="mr-2 h-4 w-4" /> Exportar mis datos</Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button data-testid="settings-delete" variant="ghost" className="text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-full">Eliminar mis datos</Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-[#0B0D13] border-white/10 text-slate-100">
              <AlertDialogHeader>
                <AlertDialogTitle className="text-white">¿Eliminar los datos de tu empresa?</AlertDialogTitle>
                <AlertDialogDescription className="text-slate-400">Esta acción registrará tu solicitud de eliminación conforme al GDPR. Nuestro equipo procesará el borrado definitivo de tu workspace y datos asociados.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="bg-white/[0.03] border-white/10 text-white">Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={() => toast.success("Solicitud de eliminación registrada.")} className="bg-red-500 text-white hover:bg-red-400">Solicitar eliminación</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </div>
  );
}
